import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { queryKeys } from '@/shared/query';
import { patientAPI } from '@/modules/patient/api/patient.api';
import type { Patient } from '@/shared/types/patient';

const SEARCH_DEBOUNCE_MS = 300;
/** Browse-on-open hits a heavy list query. Wait until the user has a real term. */
export const MIN_PATIENT_SEARCH_LENGTH = 2;

const mergeKeepSelected = (
  list: Patient[],
  keep: Patient | null | undefined
): Patient[] => {
  if (!keep?._id) return list;
  if (list.some((p) => p._id === keep._id)) return list;
  return [keep, ...list];
};

export type UsePatientSearchParams = {
  selectedPatientId: string;
  /** Prefill / edit seed so the selected patient appears without browsing. */
  seedPatientId?: string | null;
  /** When false, skip seed fetch and search (parent owns another controller). */
  enabled?: boolean;
};

export type UsePatientSearchResult = {
  options: Patient[];
  selectedPatient: Patient | null;
  /** Loaded seed document (may be set before parent wires value). */
  seedPatient: Patient | null;
  searching: boolean;
  /** True until the typed term is long enough to search. */
  queryTooShort: boolean;
  /** True after the first search settles. */
  listReady: boolean;
  seedPending: boolean;
  seedError: boolean;
  handleInputChange: (_event: unknown, value: string, reason: string) => void;
  handleOpen: () => void;
  rememberSelected: (patient: Patient | null) => void;
};

/**
 * Debounced patient search for Autocomplete pickers.
 * Does not load a list on open. Searches after the typed term is long enough.
 * Aborts in-flight requests; seeds selected patient via get-by-id.
 */
export const usePatientSearch = ({
  selectedPatientId,
  seedPatientId = null,
  enabled = true,
}: UsePatientSearchParams): UsePatientSearchResult => {
  const seedId = enabled ? seedPatientId || selectedPatientId || null : null;

  const [options, setOptions] = useState<Patient[]>([]);
  const [searching, setSearching] = useState(false);
  const [queryTooShort, setQueryTooShort] = useState(true);
  const [listReady, setListReady] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const selectedRef = useRef<Patient | null>(null);

  const seedQuery = useQuery({
    queryKey: queryKeys.patient.detail(seedId ?? ''),
    queryFn: async () => {
      const res = await patientAPI.getPatient(seedId!);
      return (res.data ?? null) as Patient | null;
    },
    enabled: Boolean(seedId),
  });

  useEffect(() => {
    const seeded = seedQuery.data;
    if (!seeded?._id) return;
    selectedRef.current = seeded;
    setOptions((prev) => mergeKeepSelected(prev, seeded));
  }, [seedQuery.data]);

  useEffect(() => {
    if (!selectedPatientId) {
      selectedRef.current = null;
      return;
    }
    const fromOptions = options.find((p) => p._id === selectedPatientId);
    if (fromOptions) {
      selectedRef.current = fromOptions;
    }
  }, [selectedPatientId, options]);

  const clearPendingSearch = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    abortRef.current?.abort();
    setSearching(false);
    setOptions((prev) => {
      const kept = mergeKeepSelected([], selectedRef.current);
      if (
        prev.length === kept.length &&
        prev.every((item, index) => item._id === kept[index]?._id)
      ) {
        return prev;
      }
      return kept;
    });
  }, []);

  const runSearch = useCallback(async (term: string) => {
    if (!enabled) return;
    const query = term.trim();
    if (query.length < MIN_PATIENT_SEARCH_LENGTH) {
      clearPendingSearch();
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setSearching(true);
    try {
      const res = await patientAPI.searchPatients(query, { signal: controller.signal });
      if (controller.signal.aborted) return;
      const list = (res.data ?? []) as Patient[];
      setOptions(mergeKeepSelected(list, selectedRef.current));
    } catch (err) {
      if (axios.isCancel(err) || controller.signal.aborted) return;
      setOptions(mergeKeepSelected([], selectedRef.current));
    } finally {
      if (!controller.signal.aborted) {
        setSearching(false);
        setListReady(true);
      }
    }
  }, [enabled, clearPendingSearch]);

  const handleInputChange = useCallback(
    (_event: unknown, value: string, reason: string) => {
      if (reason === 'clear') {
        setQueryTooShort(true);
        clearPendingSearch();
        return;
      }
      if (reason !== 'input') return;
      if (timerRef.current) clearTimeout(timerRef.current);
      const query = value.trim();
      const tooShort = query.length < MIN_PATIENT_SEARCH_LENGTH;
      setQueryTooShort(tooShort);
      if (tooShort) {
        clearPendingSearch();
        return;
      }
      setSearching(true);
      timerRef.current = setTimeout(() => {
        void runSearch(query);
      }, SEARCH_DEBOUNCE_MS);
    },
    [clearPendingSearch, runSearch]
  );

  /** Opening the menu does not fetch. Results appear after the user types. */
  const handleOpen = useCallback(() => {}, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
  }, []);

  const selectedPatient =
    options.find((p) => p._id === selectedPatientId) ??
    (seedQuery.data?._id === selectedPatientId ? seedQuery.data : null) ??
    null;

  return {
    options,
    selectedPatient,
    seedPatient: seedQuery.data ?? null,
    searching,
    queryTooShort,
    listReady,
    seedPending: Boolean(seedId) && seedQuery.isPending,
    seedError: seedQuery.isError,
    handleInputChange,
    handleOpen,
    rememberSelected: (patient: Patient | null) => {
      selectedRef.current = patient;
      if (patient) {
        setOptions((prev) => mergeKeepSelected(prev, patient));
      }
    },
  };
};
